import React from 'react'
import { Typography } from '@mui/material'
import {
  CancelMajor,
  ChevronRightMinor,
  CircleAlertMajor,
  CircleCancelMajor,
  CircleInformationMajor,
  CircleTickMajor,
} from '@shopify/polaris-icons'
import tw from 'twin.macro'

export type InfoContainerColor = 'purple' | 'orange' | 'success' | 'error' | 'info'

export interface InfoContainerProps {
  children?: React.ReactNode
  className?: any
  color?: InfoContainerColor
  actionText?: string
  icon?: React.ReactNode
  onClick?(): void
  onClose?(): void
  dataTestId?: string
  useInfoIcon?: boolean
}

const containerStyles = {
  purple: tw`bg-primary-100`,
  orange: tw`bg-warning-200`,
  success: tw`bg-success-200`,
  error: tw`bg-critical-50`,
  info: tw`bg-info-200`,
}

const containerHoverStyles = {
  purple: tw`bg-primary-100 hover:bg-primary-200 cursor-pointer`,
  orange: tw`cursor-pointer`,
  success: tw`cursor-pointer`,
  error: tw`bg-critical-50 cursor-pointer`,
  info: tw`bg-info-200 cursor-pointer`,
}

const textStyles = {
  purple: tw`text-primary-500`,
  orange: tw`text-warning-500`,
  success: tw`text-success-500`,
  error: tw`text-critical-500`,
  info: tw`text-info-500`,
}

const iconStyles = {
  purple: tw`fill-primary-500`,
  orange: tw`fill-warning-500`,
  success: tw`fill-success-500`,
  error: tw`fill-critical-500`,
  info: tw`fill-info-500`,
}

export const InfoContainer = ({
  children,
  className,
  actionText,
  color = 'purple',
  icon,
  onClick,
  onClose,
  dataTestId,
  useInfoIcon,
}: InfoContainerProps) => {
  const handleClose = (event: React.MouseEvent) => {
    event.stopPropagation()
    onClose?.()
  }

  const getIcon = () => {
    if (icon) {
      return icon
    }

    if (color === 'success') {
      return <CircleTickMajor width={20} height={20} css={tw`fill-success-500`} />
    }

    if (color === 'error') {
      return <CircleCancelMajor width={20} height={20} css={iconStyles[color]} />
    }

    if (useInfoIcon) {
      return <CircleInformationMajor width={20} height={20} css={iconStyles[color]} />
    }

    return <CircleAlertMajor width={20} height={20} css={iconStyles[color]} />
  }

  return (
    <div
      css={[
        tw`p-4 rounded-lg flex flex-row items-center`,
        onClick ? containerHoverStyles[color] : containerStyles[color],
        className,
      ]}
      onClick={onClick}
      {...(dataTestId ? { 'data-testid': dataTestId } : {})}
    >
      <div css={tw`w-5 mr-4 h-5`}>{getIcon()}</div>
      <div css={tw`flex flex-col`}>
        <Typography css={textStyles[color]}>{children}</Typography>
        {actionText && (
          <Typography css={[tw`mt-2`, textStyles[color]]} fontWeight={600}>
            {actionText}
          </Typography>
        )}
      </div>
      {onClick ? (
        <div css={tw`ml-auto h-[20px]`}>
          <ChevronRightMinor width={20} css={iconStyles[color]} />
        </div>
      ) : null}
      {onClose ? (
        <button
          type="button"
          aria-label="Dismiss"
          css={tw`ml-auto -my-3 -mr-3 w-11 h-11 shrink-0 self-start flex items-center justify-center bg-transparent border-0 cursor-pointer`}
          onClick={handleClose}
          data-testid={dataTestId ? `${dataTestId}-close-btn` : undefined}
        >
          <CancelMajor width={20} height={20} css={iconStyles[color]} />
        </button>
      ) : null}
    </div>
  )
}
